import React from "react";
import { Achievement, Team } from "../types/tournament";
import { Trophy, Shield, Target, Star } from "lucide-react";

interface AchievementDisplayProps {
  achievements: Achievement[];
  teams: Team[];
}

const AchievementDisplay: React.FC<AchievementDisplayProps> = ({
  achievements,
  teams,
}) => {
  const getAchievementIcon = (type: string) => {
    switch (type) {
      case "perfect_game":
        return <Trophy className="w-6 h-6 text-yellow-500" />;
      case "iron_wall":
        return <Shield className="w-6 h-6 text-blue-500" />;
      case "comeback":
        return <Target className="w-6 h-6 text-red-500" />;
      case "streak":
        return <Star className="w-6 h-6 text-purple-500" />;
      default:
        return <Trophy className="w-6 h-6 text-gray-500" />;
    }
  };

  const getAchievementColor = (type: string) => {
    switch (type) {
      case "perfect_game":
        return "bg-yellow-100 border-yellow-300 text-yellow-800";
      case "iron_wall":
        return "bg-blue-100 border-blue-300 text-blue-800";
      case "comeback":
        return "bg-red-100 border-red-300 text-red-800";
      case "streak":
        return "bg-purple-100 border-purple-300 text-purple-800";
      default:
        return "bg-gray-100 border-gray-300 text-gray-800";
    }
  };

  const getTeamName = (achievement: Achievement) => {
    // Find which team earned this achievement
    const team = teams.find((t) =>
      t.achievements.some((a) => a.id === achievement.id)
    );
    return team?.name || "Unknown Team";
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Trophy className="w-5 h-5 mr-2" />
          Tournament Achievements
        </h3>
        <p className="text-gray-600 mb-6">
          Special accomplishments and milestones earned during the tournament
        </p>
      </div>

      {achievements.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No achievements earned yet</p>
          <p className="text-gray-400 text-sm mt-2">
            Teams will earn achievements as they play matches
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`border-2 rounded-lg p-6 ${getAchievementColor(
                achievement.type
              )}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  {getAchievementIcon(achievement.type)}
                  <span className="ml-2 text-2xl">{achievement.icon}</span>
                </div>
                <div className="text-sm opacity-75">
                  {formatDate(achievement.earnedAt)}
                </div>
              </div>

              <h4 className="text-lg font-bold mb-2">{achievement.name}</h4>
              <p className="text-sm mb-3 opacity-90">
                {achievement.description}
              </p>

              <div className="text-sm font-medium">
                Earned by: {getTeamName(achievement)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Achievement Progress */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Achievement Progress
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-gray-900 mb-3">Perfect Games</h5>
            <div className="space-y-2">
              {teams
                .filter((team) => team.perfectGames > 0)
                .sort((a, b) => b.perfectGames - a.perfectGames)
                .map((team) => (
                  <div
                    key={team.id}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm">{team.name}</span>
                    <span className="text-sm font-semibold text-yellow-600">
                      {team.perfectGames} 🏆
                    </span>
                  </div>
                ))}
              {teams.filter((team) => team.perfectGames > 0).length === 0 && (
                <p className="text-sm text-gray-500">No perfect games yet</p>
              )}
            </div>
          </div>

          <div>
            <h5 className="font-medium text-gray-900 mb-3">Court 1 Streaks</h5>
            <div className="space-y-2">
              {teams
                .filter((team) => team.court1Streak > 0)
                .sort((a, b) => b.court1Streak - a.court1Streak)
                .map((team) => (
                  <div
                    key={team.id}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm">{team.name}</span>
                    <span className="text-sm font-semibold text-blue-600">
                      {team.court1Streak} 🛡️
                    </span>
                  </div>
                ))}
              {teams.filter((team) => team.court1Streak > 0).length === 0 && (
                <p className="text-sm text-gray-500">No court 1 streaks yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementDisplay;
